<template>
  <div
    :class="{
      'uss-persona-edu-intent container mt-6': true,
    }"
  >
    <h2>
      {{ personaGeneralTitle }}
    </h2>
    <p
      :class="{
        'mb-4 text-center text-xs leading-7 text-www-color-neutral-medium-dark md:text-15px': true,
      }"
    >
      {{ personaGeneralSubtitle }}
    </p>
    <div
      :class="{
        'relative py-9': true,
      }"
    >
      <UssPersonaObject
        v-for="(persona, index) in personaList"
        :key="index"
        v-bind="persona"
        :is-selected="selectedPersonaTitle === persona.personaTitle"
        :is-highlighted="
          highlightedPersona === persona.personaTitle ||
          (!highlightedPersona && selectedPersonaTitle === persona.personaTitle) ||
          (!highlightedPersona && !selectedPersonaTitle && index === 0)
        "
        @select="onSelectPersona"
        @hover="highlightedPersona = $event.personaTitle"
        @hover-end="highlightedPersona = ''"
      />
    </div>
    <p
      :class="{
        'mb-25px mt-12 text-center text-xs text-www-color-neutral-medium-dark md:text-lg': true,
      }"
    >
      {{ PersonaListSubTitle }}
    </p>
    <UssPersonaEduList
      :personas="eduList"
      :selected-value="selectedEduLevel"
      @select="onSelectEduLevel"
    />

    <div class="hekate-placeholder"></div>
    <slot />
    <template v-if="screenPartialData?.sectionBuilder?.length > 0">
      <component
        :is="getComponent(component._type)"
        v-for="component in screenPartialData?.sectionBuilder"
        :key="component._key"
        :value="component"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { FieldHtmlNameUtil } from '@onshape/www-inertia';
import UssPersonaEduList from './UssPersonaEduList.vue';
import UssPersonaObject from '~/components/Atoms/SignUpPartials/UssPersona/UssPersonaObject.vue';
import type PersonaEduIntentSignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/PersonaEduIntentSignupScreen';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type { SignupPartialViewsSectionBuilder, UssPersonaEduIntent } from '~/sanity/types';
import { getComponent } from '~/components/Mapper.js';

const { signupScreen } = defineProps<
  UssPersonaEduIntent & {
    signupScreen: PersonaEduIntentSignupScreen;
    sessionInfo: UnifiedSignupSessionInfo;
    screenPartialData: SignupPartialViewsSectionBuilder;
  }
>();
const experienceField = signupScreen.getField(FieldHtmlNameUtil.whatIsYourExperience);
const personaDetailField = signupScreen.getField(FieldHtmlNameUtil.personaDetail);
const eduLevelField = signupScreen.getField(FieldHtmlNameUtil.eduLevel);
const ageVerificationField = signupScreen.getField(FieldHtmlNameUtil.ageVerification);

const selectedPersonaTitle = ref<string>('');
const highlightedPersona = ref<string>('');
const selectedEduLevel = ref<string>(eduLevelField.val as string);
const selectedPersonaValue = ref<string>(experienceField.val as string);

const onSelectPersona = (args: {
  persona: string;
  personaDetails: string;
  personaTitle: string;
}) => {
  selectedPersonaTitle.value = args.personaTitle;
  selectedPersonaValue.value = args.persona;
  experienceField.val = args.persona;
  personaDetailField.val = args.personaDetails;
};

const onSelectEduLevel = (value: string) => {
  selectedEduLevel.value = value;
  eduLevelField.val = value;
};

// Watch for K-12 + Student combination to show age verification
watch(
  [selectedEduLevel, selectedPersonaValue],
  ([eduLevel, experience]) => {
    if (`${eduLevel}`.includes('K-12') && `${experience}`.toLowerCase().includes('student')) {
      ageVerificationField.componentName = ageVerificationField.initialComponentName;
      ageVerificationField.val = '';
    } else {
      ageVerificationField.val = 'overage';
      ageVerificationField.componentName = 'hidden-field';
    }
  },
  { immediate: true }
);
</script>

<style></style>
